import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-hot-toast";
import "./PatientRegistration.css";
import { IconUser, IconMail, IconPhone, IconShield, IconRegister, IconBack } from "../../Component/shared/icons/Index";
import LoginDatePicker from "../../Component/shared/loginDatePicker/Index";
import { getSessionData } from "../../helpers/Util";
import { RegisterPatient, SendPatientRegistrationOtp } from "./service";
import { CallApiForOTPVerification, CallSearchPatientsByContactDetails } from "../../helpers/http";
import { setSessionStorage } from "../../helpers/storage";
import { useNavigate } from "react-router-dom";
import PatientSelection from "../PatientSelection";


// MAIN PATIENT REGISTRATION COMPONENT
const PatientRegistration = ({ onSignIn, onBack }) => {

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [registerError, setRegisterError] = useState("");
  const [otpError, setOtpError] = useState("");
  const [formData, setFormData] = useState({
    firstName: "", lastName: "", dateOfBirth: "", email: "", phoneNumber: "",
  });
  const [focused, setFocused] = useState({});

  const [showOtpScreen, setShowOtpScreen] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [countdown, setCountdown] = useState(0);
  const inputRefs = useRef([]);
  const timerRef = useRef(null);
  const [patientList, setPatientList] = useState([]);
  const [authTokens, setAuthTokens] = useState({});
  const [showPatientSelection, setShowPatientSelection] = useState(false);
  

  const navigate = useNavigate();


  useEffect(() => { return () => clearInterval(timerRef.current); }, []);

  const RequiredStar = () => (
    <span style={{ color: "#e05252", marginLeft: 3, fontWeight: 700 }}>*</span>
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
    if (name === "email" && errors.phoneNumber === "Email or phone number is required") {
      setErrors(prev => ({ ...prev, phoneNumber: "" }));
    }
    if (registerError) setRegisterError("");
  };

  const handleDobChange = (val) => {
    setFormData(prev => ({ ...prev, dateOfBirth: val }));
    if (errors.dateOfBirth) setErrors(prev => ({ ...prev, dateOfBirth: "" }));
    if (registerError) setRegisterError("");
  };

  const validateForm = () => {
    const newErrors = {};
    const { firstName, lastName, dateOfBirth, email, phoneNumber } = formData;
    if (!firstName.trim()) newErrors.firstName = "First name is required";
    if (!lastName.trim()) newErrors.lastName = "Last name is required";
    if (!dateOfBirth) newErrors.dateOfBirth = "Date of birth is required";

    const hasEmail = email && email.trim();
    const hasPhone = phoneNumber && phoneNumber.trim();

    if (!hasEmail && !hasPhone) {
      newErrors.email = "Email or phone number is required";
      newErrors.phoneNumber = "Email or phone number is required";
    } else {
      if (hasEmail) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email.trim())) {
          newErrors.email = "Please enter a valid email address";
        }
      }

      if (hasPhone) {
        const digits = phoneNumber.replace(/\D/g, "");
        if (digits.length !== 10) {
          newErrors.phoneNumber = "Please enter a valid 10-digit phone number";
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const startCountdown = (seconds = 40) => {
    clearInterval(timerRef.current);
    setCountdown(seconds);
    timerRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) { clearInterval(timerRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const maskEmail = (email) => {
    if (!email || !email.includes("@")) return "";
    const [name, domainFull] = email.split("@");
    const domainParts = domainFull.split(".");
    const extension = domainParts.pop();
    const domain = domainParts.join(".");
    return `${name.slice(0, 3)}***@***${domain.slice(-3)}.${extension}`;
  };

  const handleChangeOTP = (value, index) => {
    if (!/^\d?$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (otpError) setOtpError("");
    if (value && index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0)
      inputRefs.current[index - 1]?.focus();
  };

  const handlePasteOtp = (e, index) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!pasted) return;
    e.preventDefault();
    const newOtp = [...otp];
    for (let i = 0; i < pasted.length; i++) {
      if (index + i < 6) newOtp[index + i] = pasted[i];
    }
    setOtp(newOtp);
    if (otpError) setOtpError("");
    inputRefs.current[Math.min(index + pasted.length, 5)]?.focus();
  };

  const handleBackToForm = () => {
    setShowOtpScreen(false);
    setOtpError("");
    setRegisterError("");
    setOtp(["", "", "", "", "", ""]);
    setCountdown(0);
    clearInterval(timerRef.current);
  };

  const formatUSPhone = (phone) => {
    if (!phone) return "";
    const digits = phone.replace(/\D/g, "").slice(0, 10);
    if (digits.length < 4) return digits;
    if (digits.length < 7) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    }
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  const onPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text");
    const digits = pasted.replace(/\D/g, "").slice(0, 10);
    setFormData(prev => ({ ...prev, phoneNumber: digits }));
    if (errors.phoneNumber) setErrors(prev => ({ ...prev, phoneNumber: "" }));
    if (errors.email === "Email or phone number is required") {
      setErrors(prev => ({ ...prev, email: "" }));
    }
    if (registerError) setRegisterError("");
  };

  const inputClass = (name) => {
    if (errors[name]) return "login-input-error";
    if (focused[name]) return "login-input-focused";
    return "login-input";
  };


  const handleResendOtp = async () => {
    setLoading(true);
    setOtpError("");
    try {
      const res = await SendPatientRegistrationOtp({ email: formData.email, phoneNumber: formData.phoneNumber });
      if (res.status === 200) {
        setOtp(["", "", "", "", "", ""]);
        inputRefs.current[0]?.focus();
        startCountdown(40);
        toast.success("OTP resent successfully!");
      } else {
        setOtpError(res?.data?.message || res?.data?.Message || "Failed to resend OTP.");
      }
    } catch (err) {
      console.error("Resend OTP error:", err);
      setOtpError("Failed to resend OTP."
      );
    } finally {
      setLoading(false);
    }
  };

    const handlePatientSelect = (patient) => {
    const tokenData = {
      ...patient,
      accessApiToken: authTokens.accessApiToken,
      commonserviceToken: authTokens.commonserviceToken,
    };
    sessionStorage.setItem("tokenData", JSON.stringify({ ...tokenData }));

    const parseTokenArray = (val) => {
      if (!val) return [];
      if (Array.isArray(val)) return val;
      try { return JSON.parse(val); } catch { return []; }
    };

    sessionStorage.setItem("tokenNodes", JSON.stringify(parseTokenArray(patient?.nodes || patient?.Nodes)));
    sessionStorage.setItem("tokenIntakeSheets", JSON.stringify(
      parseTokenArray(
        patient?.intakeSheets ||
        patient?.IntakeSheets ||
        patient?.intakeSheet ||
        patient?.IntakeSheet
      )
    ));
    sessionStorage.setItem("tokenDocuments", JSON.stringify(
      parseTokenArray(
        patient?.documents ||
        patient?.Documents ||
        patient?.document  ||
        patient?.Document
      )
    ));
    sessionStorage.setItem("tokenRateReviewTemplate", JSON.stringify(
      parseTokenArray(patient?.rateReviewTemplate || patient?.RateReviewTemplate)[0] || null
    ));

    navigate("/multiPatientPortal");
  };

  const handleRegister = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setErrors({});
    setRegisterError("");
    setOtpError("");
    if (!validateForm()) return;

    setLoading(true);
    const tokenData = getSessionData('tokenData', {});
    const registrationData = {
      caseId: formData.caseId,
      firstName: formData.firstName,
      lastName: formData.lastName,
      dateOfBirth: formData?.dateOfBirth ? new Date(formData.dateOfBirth).toISOString() : "",
      email: formData.email,
      phoneNumber: formData.phoneNumber,
      companyId: tokenData?.companyId,
      canRegister: false,
    };

    try {
      const regRes = await RegisterPatient(registrationData);
      if (regRes.status === 200) {
        const canReg = regRes?.data?.canRegister ?? regRes?.data?.CanRegister;
        if (canReg) {
          const otpRes = await SendPatientRegistrationOtp({
            email: formData.email,
            phoneNumber: formData.phoneNumber,
          });
          if (otpRes.status === 200) {
            setShowOtpScreen(true);
            setOtp(["", "", "", "", "", ""]);
            startCountdown(40);
            setTimeout(() => inputRefs.current[0]?.focus(), 100);
          } else {
            setShowOtpScreen(false);
            setRegisterError("Failed to send OTP.");
          }
        } else {
          setShowOtpScreen(false);
          setRegisterError(regRes?.data?.message || "Registration failed."
          );
        }
      } else {
        setShowOtpScreen(false);
        setRegisterError("Registration failed."
        );
      }
    } catch (regErr) {
      console.error("RegisterPatient error:", regErr);
      setShowOtpScreen(false);
      setRegisterError("Registration failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyAndRegister = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setOtpError("");
    setRegisterError("");
    const otpValue = otp.join("");
    if (otpValue.length !== 6) {
      setOtpError("Please enter the complete 6-digit OTP.");
      return;
    }
    setLoading(true);
    try {
      let verifyRes;
      try {
        verifyRes = await CallApiForOTPVerification({
          email: formData.email,
          phoneNumber: formData.phoneNumber,
          otp: otpValue,
        });
        if (verifyRes?.success) {
          // toast.success("OTP verified!");
        }
      } catch (verifyErr) {
        console.error("OTP verification error:", verifyErr);
        const msg =
          verifyErr?.message === "Invalid or expired OTP"
            ? "Invalid or Expired OTP."
            : "Something went wrong.";
        setOtpError(msg);
        setOtp(["", "", "", "", "", ""]);
        inputRefs.current[0]?.focus();
        return;
      }

      if (!verifyRes?.success) {
        setOtpError("Something went wrong.");
        setOtp(["", "", "", "", "", ""]);
        inputRefs.current[0]?.focus();
        return;
      }

      const tokenData = getSessionData('tokenData', {});
      const registrationData = {
        caseId: formData.caseId,
        firstName: formData.firstName,
        lastName: formData.lastName,
        dateOfBirth: formData?.dateOfBirth ? new Date(formData.dateOfBirth).toISOString() : "",
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        companyId: tokenData?.companyId,
        canRegister: true,
      };

      try {
        const regRes = await RegisterPatient(registrationData);
        if (regRes.status === 200) {
          const regId = regRes?.data?.registrationId ?? regRes?.data?.RegistrationId;
          const isSuccess = regRes?.data?.success ?? regRes?.data?.Success ?? (regId > 0);
          if (regId > 0 || isSuccess) {
            toast.success("Registered successfully!");
            setShowOtpScreen(true);
            setErrors({});
            setFocused({});
            setRegisterError("");
            setOtpError("");
            // setOtp(["", "", "", "", "", ""]);
            setCountdown(0);
            clearInterval(timerRef.current);
            await handleSearchPatient();
          } else {
            setOtpError("Registration failed.");
          }
        } else {
          setOtpError("Registration failed.");
        }
      } catch (regErr) {
        console.error("RegisterPatient error:", regErr);
        setOtpError("Registration failed.");
      }
    } finally {
      setLoading(false);
    }
  };

const handleSearchPatient = async () => {
  try{
        const res = await CallSearchPatientsByContactDetails({ ...formData, sendOtp: false });
        if (res?.success) {
          const data = res?.data.data;
          const accessApiToken = data?.accessApiToken;
          const commonserviceToken = data?.commonserviceToken;
          const rawPatients = data?.patients || [];
          sessionStorage.setItem("allPatientsList", JSON.stringify(rawPatients));
          sessionStorage.setItem("authTokens", JSON.stringify({ accessApiToken, commonserviceToken }));
          setSessionStorage('accessApiToken', accessApiToken);
          setSessionStorage('commonServiceToken', commonserviceToken);
  
          const uniquePatientsMap = new Map();
          rawPatients.forEach(p => {
            const id = p.patientId || p.PatientId;
            if (id && !uniquePatientsMap.has(id)) {
              uniquePatientsMap.set(id, p);
            } else if (!id) {
              uniquePatientsMap.set(Math.random(), p);
            }
          });
          const patients = Array.from(uniquePatientsMap.values());
          setPatientList(patients);
          setAuthTokens({ accessApiToken, commonserviceToken });
  
          if (patients.length === 1) {
            const patientData = patients[0];
            const tokenData = {
              ...patientData,
              accessApiToken,
              commonserviceToken,
            };
            // sessionStorage.setItem("tokenData", JSON.stringify({ ...tokenData }));
            setSessionStorage('tokenData', tokenData);
            setSessionStorage('accessApiToken', accessApiToken);
            setSessionStorage('commonserviceToken', commonserviceToken);
  
            const parseTokenArray = (val) => {
              if (!val) return [];
              if (Array.isArray(val)) return val;
              try { return JSON.parse(val); } catch { return []; }
            };
  
            sessionStorage.setItem("tokenNodes", JSON.stringify(parseTokenArray(patientData?.nodes || patientData?.Nodes)));
            sessionStorage.setItem("tokenIntakeSheets", JSON.stringify(
              parseTokenArray(
                patientData?.intakeSheets ||
                patientData?.IntakeSheets ||
                patientData?.intakeSheet ||
                patientData?.IntakeSheet
              )
            ));
            sessionStorage.setItem("tokenDocuments", JSON.stringify(
              parseTokenArray(
                patientData?.documents ||
                patientData?.Documents ||
                patientData?.document ||
                patientData?.Document
              )
            ));
              sessionStorage.setItem("tokenRateReviewTemplate", JSON.stringify(
              parseTokenArray(patientData?.rateReviewTemplate || patientData?.RateReviewTemplate)[0] || null
            ));
          }

          const tokenData = getSessionData('tokenData', {})          
          localStorage.setItem("userData", JSON.stringify({
          caseId: formData.caseId,
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phoneNumber: formData.phoneNumber,
        }));
        localStorage.setItem("patientDetails", JSON.stringify({
          DoctorName: formData.doctorName,
          DoctorSpecialtyName: formData.doctorSpecialtyName,
          ProviderName: formData.providerName,
          ...(res.patientDetails || {}),
        }));
        const allPatientsList = getSessionData('allPatientsList', []);
        let IsOnlyPatient = false;

        if (allPatientsList && allPatientsList.length > 0) {
          const uniqueCaseIds = [...new Set(allPatientsList.map(p => p.caseId || p.CaseId).filter(Boolean))];
          IsOnlyPatient = uniqueCaseIds.length === 1;
        }

        if (tokenData?.isLinkData) {
          navigate("/patientPortal");
        }
        else {
          if (IsOnlyPatient) {
            navigate("/multiPatientPortal");
          }
          else {
            setShowPatientSelection(true);
          }
        }
        } else {
          setOtpError("No matching record found.");
          // toast.error("Patient not found");
        }
      }
      catch (err) {
        console.error("CallSearchPatientsByContactDetails error:", err);
        setOtpError("Something went wrong.");
      }
      }

  const Spinner = () => (
    <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
      <svg width="18" height="18" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.3)"></circle>
        <path d="M12 2a10 10 0 0 1 10 10" stroke="#fff">
          <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite" />
        </path>
      </svg>
    </span>
  );

  if (showOtpScreen) {
    return (
      <div className="login-page">
        <div className="login-card">
          <div className="login-top-bar" />

          <div className="login-otp-icon-wrap">
            <div className="login-otp-icon-circle">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#2d8a5e" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                <polyline points="9 12 11 14 15 10" />
              </svg>
            </div>
          </div>

          <h2 className="login-otp-heading">Verify it's you, {formData.firstName}</h2>
          <p className="login-otp-sub">A 6-digit OTP has been sent to</p>

          <div className="login-otp-contact-cards">
            {formData.phoneNumber && formData.phoneNumber.trim() !== "" && (
              <div className="login-otp-contact-card">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2d8a5e" strokeWidth="2">
                  <rect x="5" y="2" width="14" height="20" rx="2" />
                  <line x1="12" y1="18" x2="12.01" y2="18" />
                </svg>
                <span className="login-otp-contact-key">Mobile</span>
                <span className="login-otp-contact-val">+1 ••••••{formData.phoneNumber.slice(-4)}</span>
              </div>
            )}
            {formData.email && formData.email.trim() !== "" && (
              <div className="login-otp-contact-card">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2d8a5e" strokeWidth="2">
                  <rect x="2" y="4" width="20" height="16" rx="2" />
                  <polyline points="2,4 12,13 22,4" />
                </svg>
                <span className="login-otp-contact-key">Email</span>
                <span className="login-otp-contact-val">{maskEmail(formData.email)}</span>
              </div>
            )}
          </div>

          <form onSubmit={handleVerifyAndRegister}>
            <p className="login-otp-code-label">Enter your 6-digit verification code</p>
            <div className="login-otp-row">
              {otp.map((digit, index) => (
                <React.Fragment key={index}>
                  <input
                    ref={(el) => (inputRefs.current[index] = el)}
                    type="text" inputMode="numeric"
                    autoComplete={index === 0 ? "one-time-code" : "off"}
                    maxLength={1} value={digit}
                    className={digit ? "login-otp-box--filled" : "login-otp-box"}
                    onChange={(e) => handleChangeOTP(e.target.value, index)}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    onPaste={(e) => handlePasteOtp(e, index)}
                  />
                </React.Fragment>
              ))}
            </div>

            <div className="login-resend-row">
              {countdown > 0 ? (
                <span>Resend code in <span className="login-resend-timer">0:{String(countdown).padStart(2, "0")}</span></span>
              ) : (
                <button type="button" className="login-resend-btn" onClick={handleResendOtp} disabled={loading}>
                  Resend OTP
                </button>
              )}
            </div>

            {otpError && (
              <div className="login-api-error">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                <span>{otpError}</span>
              </div>
            )}

            <button
              type="submit"
              className={`login-btn-primary${loading || otp.join("").length !== 6 ? " login-btn-primary--disabled" : ""}`}
              disabled={loading || otp.join("").length !== 6}
            >
              {loading ? <><Spinner /> Verifying...</> : "Verify & Register"}
            </button>
          </form>

          <div className="login-back-btn-wrap">
            <button className="login-back-btn" onClick={handleBackToForm}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="15 18 9 12 15 6" />
              </svg>
              {" "}Back to Registration
            </button>
          </div>

        </div>
      </div>
    );
  }

  return (
        
      <div className="login-page">
        <div className="login-card">
          <div className="login-top-bar" />
                  {showPatientSelection ? (
          <PatientSelection patients={patientList} onSelect={handlePatientSelect} />

        )  : <>

          {onBack && (
            <div className="ml-top-back-wrap">
              <button
                type="button"
                className="ml-top-back-btn"
                onClick={onBack}
              >
                <IconBack /> Back
              </button>
            </div>
          )}

          {/* Header */}
          <div className="manual-login-header-container">
            <div className="login-header-text">
              <h2 className="login-heading" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", marginBottom: "6px" }}>
                <IconRegister width={22} height={22} />
                Registration Form
              </h2>
              <p className="login-subtitle">
                {[formData.doctorName, formData.doctorSpecialtyName, formData.providerName]
                  .filter(Boolean)
                  .join(" • ")}
              </p>
            </div>
          </div>

          <form onSubmit={handleRegister} noValidate>
            <div className="login-input-row">
              <div>
                <label className="login-label">First Name <RequiredStar /></label>
                <div className="login-input-wrap">
                  <span className="login-input-icon"><IconUser /></span>
                  <input
                    className={inputClass("firstName")}
                    type="text" name="firstName" placeholder="First name"
                    value={formData.firstName} autoComplete="given-name"
                    onChange={handleChange}
                    onFocus={() => setFocused(f => ({ ...f, firstName: true }))}
                    onBlur={() => setFocused(f => ({ ...f, firstName: false }))}
                  />
                </div>
                {errors.firstName && <span className="login-error-text">{errors.firstName}</span>}
              </div>
              <div>
                <label className="login-label">Last Name <RequiredStar /></label>
                <div className="login-input-wrap">
                  <span className="login-input-icon"><IconUser /></span>
                  <input
                    className={inputClass("lastName")}
                    type="text" name="lastName" placeholder="Last name"
                    value={formData.lastName} autoComplete="family-name"
                    onChange={handleChange}
                    onFocus={() => setFocused(f => ({ ...f, lastName: true }))}
                    onBlur={() => setFocused(f => ({ ...f, lastName: false }))}
                  />
                </div>
                {errors.lastName && <span className="login-error-text">{errors.lastName}</span>}
              </div>
            </div>


            <div className="login-field-group">
              <label className="login-label">Email Address</label>
              <div className="login-input-wrap">
                <span className="login-input-icon"><IconMail /></span>
                <input
                  className={inputClass("email")}
                  type="email" name="email" placeholder="Enter your email address"
                  value={formData.email} autoComplete="email" inputMode="email"
                  onChange={handleChange}
                  onFocus={() => setFocused(f => ({ ...f, email: true }))}
                  onBlur={() => setFocused(f => ({ ...f, email: false }))}
                />
              </div>
              {errors.email && <span className="login-error-text">{errors.email}</span>}
            </div>


            <div className="login-field-group">
              <label className="login-label">Phone Number</label>
              <div className="login-input-wrap">
                <span className="login-input-icon"><IconPhone /></span>
                <span className="login-phone-prefix">+1</span>
                <input
                  className={
                    errors.phoneNumber
                      ? "login-input-error login-input-phone-error"
                      : "login-input-phone"
                  }
                  type="tel" name="phoneNumber" placeholder="10-digit number"
                  value={formatUSPhone(formData.phoneNumber)} inputMode="numeric" autoComplete="tel-national"
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
                    setFormData(prev => ({ ...prev, phoneNumber: digits }));
                    if (errors.phoneNumber) setErrors(prev => ({ ...prev, phoneNumber: "" }));
                    if (errors.email === "Email or phone number is required") {
                      setErrors(prev => ({ ...prev, email: "" }));
                    }
                    if (registerError) setRegisterError("");
                  }}
                  onPaste={onPaste}
                  onFocus={() => setFocused(f => ({ ...f, phoneNumber: true }))}
                  onBlur={() => setFocused(f => ({ ...f, phoneNumber: false }))}
                />
              </div>
              {errors.phoneNumber && <span className="login-error-text">{errors.phoneNumber}</span>}
            </div>

            <div className="login-field-group">
              <label className="login-label">
                Date of Birth <RequiredStar />
              </label>
              <LoginDatePicker
                value={formData.dateOfBirth}
                onChange={handleDobChange}
                hasError={!!errors.dateOfBirth}
              />
              {errors.dateOfBirth && <span className="login-error-text">{errors.dateOfBirth}</span>}
            </div>

            {registerError && (
              <div className="login-api-error">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                <span>{registerError}</span>
              </div>
            )}

            <button
              type="submit"
              className={`login-btn-primary${loading ? " login-btn-primary--disabled" : ""}`}
              disabled={loading}
            >
              {loading ? <><Spinner /> Loading...</> : "Proceed to OTP Verification →"}
            </button>
          </form>

          {onSignIn && (
            <div className="pr-signin-row">
              <span className="pr-signin-text">Already registered?</span>
              <button
                type="button"
                className="pr-signin-btn"
                onClick={() => {
                  onSignIn();
                  setRegisterError("");
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
                  <polyline points="10 17 15 12 10 7" />
                  <line x1="15" y1="12" x2="3" y2="12" />
                </svg>
                Sign In
              </button>
            </div>
          )}
              <p className="login-footer"><IconShield />Your information is encrypted and secure.</p>

          </>
          }
              </div>
            </div>


  );
};

export default PatientRegistration;